import React, { useEffect, useState } from 'react';
import tw from 'twin.macro';
import { StepWrapper } from 'containers/Demarrage/components/StepWrapper';
import {
  useCountries,
  getScholarSystem,
  getDefaultZone,
} from 'containers/useCountries';
import Select2 from '@teetsh/app/src/components/Form/Select2';
import { useTranslation } from 'react-i18next';
import { useUserAccount } from '@teetsh/app/src/queries/user/account/useUserAccount';
import useUpdateUserAccount from '@teetsh/app/src/queries/user/account/useUpdateUserAccount';
import { useDemarrage } from '@teetsh/app/src/containers/Demarrage/queries/useDemarrage';
import { useUpdateDemarrage } from '@teetsh/app/src/containers/Demarrage/queries/useUpdateDemarrage';
import {
  getCountriesForSelection,
  useGetFrenchZones,
  determineInitialCountryAndZone,
} from '@teetsh/app/src/utils/localization';
import { ScholarSystemValue } from 'containers/scholarSystem';
import { ScholarSystemSelector } from './ScholarSystemSelector';

export function Pays() {
  const { t } = useTranslation(['countries', 'onboarding', 'common']);
  const { sortedCountries: sortedCountriesAndZones } = useCountries();
  const { data: userAccount, isLoading: userAccountLoading } = useUserAccount();
  const updateUserAccount = useUpdateUserAccount();
  const { data: demarrageData, isLoading: demarrageLoading } = useDemarrage();
  const updateDemarrage = useUpdateDemarrage();
  const getFrenchZones = useGetFrenchZones();

  const [countryCode, setCountryCode] = useState<string>('');
  const [zone, setZone] = useState<string | undefined>(undefined);
  const [scholarSystem, setScholarSystem] = useState<
    ScholarSystemValue | undefined
  >(undefined);

  const sortedCountries = getCountriesForSelection(sortedCountriesAndZones);
  const sortedFRZones = getFrenchZones(sortedCountriesAndZones);

  useEffect(() => {
    if (userAccount && demarrageData && countryCode === '') {
      // Only set initial values if not already set
      const { country, zone: initialZone } = determineInitialCountryAndZone(
        demarrageData.userCountryCode,
        t('common:countryCode'),
        sortedFRZones,
      );

      const initialScholarSystem =
        demarrageData?.scholarSystem ?? getScholarSystem(country);

      if (initialScholarSystem && initialScholarSystem !== scholarSystem) {
        setScholarSystem(initialScholarSystem);
      }
      setCountryCode(country);
      if (initialZone) {
        setZone(initialZone);
      }

      updateDemarrage({
        userCountryCode: country,
        scholarSystem: initialScholarSystem,
      });
    }
  }, [userAccount, demarrageData, sortedFRZones, t]);

  if (
    userAccountLoading ||
    demarrageLoading ||
    !userAccount ||
    !demarrageData
  ) {
    return null;
  }

  const handleCountryChange = async (newCountryCode: string) => {
    setCountryCode(newCountryCode);

    if (newCountryCode === 'FR') {
      setZone('FR');
    } else {
      setZone(undefined);
    }

    const newScholarSystem =
      newCountryCode !== countryCode
        ? getScholarSystem(newCountryCode) ?? 'FR'
        : scholarSystem;

    setScholarSystem(newScholarSystem);

    // Update demarrage data with country code
    await updateDemarrage({
      userCountryCode: newCountryCode,
      zoneId: getDefaultZone(newCountryCode),
      scholarSystem: newScholarSystem,
    });

    // Update user account with locale
    if (userAccount) {
      await updateUserAccount({
        email: userAccount.email,
        location: userAccount.location,
        optin: userAccount.optin,
        givenName: userAccount.givenName || '',
        familyName: userAccount.familyName || '',
      });
    }
  };

  const handleScholarSystemChange = async (
    newScholarSystem: ScholarSystemValue,
  ) => {
    if (newScholarSystem === scholarSystem) {
      return;
    }
    setScholarSystem(newScholarSystem);
    await updateDemarrage({
      scholarSystem: newScholarSystem,
    });
  };

  const handleZoneChange = async (newZone: string) => {
    setZone(newZone);

    // Update demarrage data with the zone as the country code
    await updateDemarrage({
      userCountryCode: newZone,
      zoneId: getDefaultZone(newZone),
    });
  };

  const nextDisabled =
    !countryCode || (countryCode === 'FR' && !zone) || !scholarSystem;

  const showZoneSelector = countryCode === 'FR';

  return (
    <StepWrapper
      currentStep="pays"
      nextDisabled={nextDisabled}
      title={t('onboarding:signup.localizationTitle')}
    >
      <div tw="grid grid-cols-1 lg:grid-cols-2 gap-2">
        <div css={[!showZoneSelector && tw`col-span-2`]}>
          <Select2
            label={t('onboarding:signup.country')}
            aria-label="Country"
            large
            tw="bg-white"
            options={sortedCountries}
            portal
            value={countryCode}
            onChange={handleCountryChange}
          />
        </div>
        {showZoneSelector && (
          <div>
            <Select2
              label={t('onboarding:signup.zone')}
              aria-label="Zone"
              large
              portal
              tw="bg-white"
              options={sortedFRZones.map(z => ({
                value: z.code,
                label: z.label,
              }))}
              value={zone}
              onChange={handleZoneChange}
            />
          </div>
        )}
        <div tw="col-span-2">
          <ScholarSystemSelector
            onChange={handleScholarSystemChange}
            scholarSystem={scholarSystem}
          />
        </div>
      </div>
    </StepWrapper>
  );
}
